import { useEffect, useMemo } from "react"
import { useQuery, useQueryClient } from "@tanstack/react-query"
import { useSearchParams } from "react-router-dom"
import {
  createGeolocationFeature,
  isLatLngQueryString,
} from "utils/geolocation"
import {
  get as getPersistValue,
  set as setPersistValue,
} from "utils/persist_store"
import { Q_USER_LOCATION } from "constant"

const DEFAULT_STALE_TIME = 1000 * 60 * 5 // 5 minutes
const KEY_USER_LOCATION_VALUE = "user_location_value"
const KEY_LAST_SCANNED_LOCATION_VALUE = "last_scanned_location_value"
const transformLocalstorageData = (value) => (value === "null" ? null : value)

export const useUserLocation = (initialLocation, options) => {
  const {
    onUpdate = () => {},
    staleTime = DEFAULT_STALE_TIME,
    update = true,
    enable = true,
  } = options

  const [searchParams] = useSearchParams()
  const queryClient = useQueryClient()

  const currentUserLocation = searchParams.get(Q_USER_LOCATION)
  const storedUserLocation = transformLocalstorageData(
    getPersistValue(KEY_USER_LOCATION_VALUE)
  )

  const { data: userLocationData, isLoading } = useQuery({
    queryKey: [KEY_USER_LOCATION_VALUE],
    queryFn: () => currentUserLocation,
    enabled: enable,
    staleTime: staleTime,
    refetchOnMount: update,
    initialData: currentUserLocation || initialLocation,
    refetchInterval: staleTime,
  })

  useEffect(() => {
    setPersistValue(KEY_USER_LOCATION_VALUE, currentUserLocation)
    if (storedUserLocation) {
      setPersistValue(KEY_LAST_SCANNED_LOCATION_VALUE, storedUserLocation)
    }
    queryClient.setQueryData([KEY_USER_LOCATION_VALUE], currentUserLocation)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []) // Run only on mount

  useEffect(() => {
    if (storedUserLocation !== userLocationData) {
      setPersistValue(KEY_LAST_SCANNED_LOCATION_VALUE, storedUserLocation)
    }
    setPersistValue(KEY_USER_LOCATION_VALUE, userLocationData)

    const lastScannedLocation = transformLocalstorageData(
      getPersistValue(KEY_LAST_SCANNED_LOCATION_VALUE)
    )
    if (isLatLngQueryString(lastScannedLocation)) {
      const lastScannedLocationFeature = createGeolocationFeature(
        lastScannedLocation,
        { name: "Last Scanned" }
      )
      onUpdate(lastScannedLocationFeature)
    }
  }, [userLocationData, storedUserLocation, queryClient, onUpdate])

  const userLocation = useMemo(() => {
    if (initialLocation) return initialLocation
    return userLocationData && isLatLngQueryString(userLocationData)
      ? createGeolocationFeature(userLocationData, { name: "Your Location" })
      : null
  }, [initialLocation, userLocationData])

  return { isLoading, userLocation }
}

useUserLocation.whyDidYouRender = true
